/**
 * Videocall WebSocket Service
 *
 * Manages the WebSocket connection to the Python videochat server,
 * including connection lifecycle, message sending, and received message
 * dispatching.
 */

/**
 * Send data to the WebSocket server with debounce for non-real-time message types.
 *
 * @param {object}   data         Message payload
 * @param {object}   ctx
 * @param {WebSocket} ctx.socket
 * @param {number}   ctx.sendDebounceTimeout   Mutable timeout ref
 * @param {number}   ctx.sendDebounceDelay
 * @param {function} ctx.supportsLiveSpeech
 * @param {boolean}  ctx.recordingWasTriggered
 * @param {function} ctx.debugLog
 */
export function sendToWebSocket(data, ctx) {
    const { socket, debugLog } = ctx;
    data = Object.assign({}, data, {
        live_call_access_token: ctx.liveCallAccessToken,
    });

    // Real-time audio streaming and session management — send immediately
    if (
        data.type === 'audio_stream' ||
        // MediaRecorder has already finalized this complete Safari turn.
        // Delaying it here only adds dead air before the assistant can start.
        data.type === 'audio' ||
        data.type === 'start_session' ||
        data.type === 'end_session' ||
        data.type === 'change_voice' ||
        data.type === 'livekit_session' ||
        data.type === 'webrtc_offer' ||
        data.type === 'save_transcription' ||
        data.type === 'save_ai_response'
    ) {
        if (socket && socket.readyState === WebSocket.OPEN) {
            const jsonData = JSON.stringify(data);
            socket.send(jsonData);
            debugLog(`📤 Sent ${data.type} immediately`);
            if (ctx.onMessageSent) ctx.onMessageSent(data, { payloadBytes: jsonData.length, immediate: true });
        } else {
            console.error(`❌ Cannot send ${data.type} - WebSocket not open`);
            if (ctx.onSendFailed) ctx.onSendFailed(data, 'websocket-not-open');
        }
        return;
    }

    // Debounce other message types
    if (ctx._debounceTimeout) clearTimeout(ctx._debounceTimeout);

    ctx._debounceTimeout = setTimeout(() => {
        debugLog("WebSocket send debounce elapsed", {
            type: data.type,
            socketState: socket ? socket.readyState : null,
            supportsLiveSpeech: ctx.supportsLiveSpeech(),
            recordingWasTriggered: ctx.recordingWasTriggered,
            transcriptionLength: data.transcription ? data.transcription.length : 0,
        });
        if (socket && socket.readyState === WebSocket.OPEN) {
            if (!ctx.supportsLiveSpeech() && !ctx.recordingWasTriggered) {
                debugLog("⚠️ Skipping send - speech recognition not supported and recording not triggered");
                return;
            }

            const jsonData = JSON.stringify(data);
            debugLog("📤 Sending to WebSocket:", {
                type: data.type,
                timestamp: data.timestamp,
                messageLength: data.type === 'text' ? data.message.length : 'N/A',
                audioSize: data.type === 'audio' ? `${Math.round(data.message.length / 1024)} KB` : 'N/A',
                totalSize: `${Math.round(jsonData.length / 1024)} KB`
            });

            socket.send(jsonData);
            if (ctx.onMessageSent) ctx.onMessageSent(data, { payloadBytes: jsonData.length, immediate: false });
        } else {
            console.error("❌ Cannot send - WebSocket not open. State:", socket ? socket.readyState : 'null');
            if (ctx.onSendFailed) ctx.onSendFailed(data, 'websocket-not-open');
        }
    }, ctx.sendDebounceDelay);
}

/**
 * Close the WebSocket connection.
 *
 * @param {WebSocket} socket
 */
export function closeWebSocket(socket) {
    if (socket) {
        socket.close();
    }
}

/**
 * Route generic server errors using stable protocol codes.
 *
 * Untyped payloads remain supported while older voice-service processes and
 * cached responses drain, but new behavior must be keyed by `code`, never by
 * mutable human-readable copy.
 *
 * @param {object} data
 * @param {object} ctx
 * @returns {boolean} Whether the payload was handled as a server error.
 */
export function routeServerError(data, ctx) {
    const isLegacyError = !data.type && !!data.error;
    const isProtocolError = data.type === 'error';

    if (!isLegacyError && !isProtocolError) {
        return false;
    }

    const nestedError = data.error && typeof data.error === 'object' ? data.error : {};
    const code = String(data.code || nestedError.code || '').trim();
    const message = String(
        data.message ||
        (typeof data.error === 'string' ? data.error : nestedError.message) ||
        data.content ||
        'Server error occurred'
    ).trim();
    const normalizedMessage = message.toLowerCase();
    const isBenign = !message ||
        normalizedMessage.includes('no active response to cancel') ||
        normalizedMessage.includes('audio is too short');

    console.error('❌ WebSocket error received:', { code: code || null, message });

    if (isBenign) {
        ctx.debugLog('🤫 Suppressing benign error:', message);
        return true;
    }

    const needsSupport = code === 'voice_providers_unavailable' ||
        (!code && normalizedMessage.includes('all providers unavailable'));

    if (needsSupport && ctx.showSupportError) {
        ctx.showSupportError();
    } else {
        ctx.showError(message);
    }

    return true;
}

/**
 * Open a WebSocket connection to the Python videochat server.
 * Sets up onopen, onmessage, onclose, onerror handlers.
 *
 * @param {object}   ctx
 * @param {string}   ctx.chatId            Chat form ID for looking up ws_url_videocall
 * @param {string}   ctx.sessionToken      Current session token
 * @param {function} ctx.debugLog
 * @param {function} ctx.showError
 * @param {function} ctx.stopLoadingAnimation
 * @param {function} ctx.showActionBtns
 * @param {function} ctx.setupRecording
 * @param {function} ctx.supportsLiveSpeech
 * @param {function} ctx.startRecording
 * @param {function} ctx.stopAudio
 * @param {function} ctx.stopRecording
 * @param {number}   ctx.reconnectAttempts     Mutable ref
 * @param {number}   ctx.maxReconnectAttempts
 * @param {boolean}  ctx.isModalOpen
 * @param {function} ctx.handleMessage         Incoming message dispatcher
 * @returns {Promise<WebSocket>}
 */
export async function startWebservice(ctx) {
    const { socket, debugLog } = ctx;

    if (socket.value && (socket.value.readyState === WebSocket.OPEN || socket.value.readyState === WebSocket.CONNECTING)) {
        debugLog("WebSocket already open or connecting (state: " + socket.value.readyState + ")");
        if (socket.value.readyState === WebSocket.OPEN) {
            ctx.stopLoadingAnimation();
            ctx.showActionBtns();
        }
        return socket.value;
    }

    const wsUrl = window.growtype_chat && window.growtype_chat[ctx.chatId]
        ? window.growtype_chat[ctx.chatId]['ws_url_videocall']
        : null;

    if (!wsUrl) {
        const errMsg = 'Video call connection configuration is missing. Please contact support.';
        if (typeof window.growtypeLogError === 'function') {
            window.growtypeLogError(errMsg, { session_token: ctx.sessionToken, source: 'videocall_connection' });
        }
        ctx.showError(errMsg);
        return null;
    }

    const connectionStartedAt = Date.now();
    debugLog("Opening WebSocket", {
        url: wsUrl,
        online: navigator.onLine,
        visibilityState: document.visibilityState,
        reconnectAttempt: ctx.reconnectAttempts.val,
        network: navigator.connection ? {
            effectiveType: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink,
            rtt: navigator.connection.rtt,
            saveData: navigator.connection.saveData,
        } : null,
    });
    const ws = new WebSocket(wsUrl);
    socket.value = ws;

    ws.onopen = async () => {
        ctx.reconnectAttempts.val = 0;
        debugLog('WebSocket opened', {
            connectionMs: Date.now() - connectionStartedAt,
            protocol: ws.protocol || null,
            extensions: ws.extensions || null,
            visibilityState: document.visibilityState,
        });
        if (ctx.onSocketEvent) ctx.onSocketEvent('open', { connectionMs: Date.now() - connectionStartedAt });

        if (ctx.supportsLiveKit && ctx.supportsLiveKit()) {
            await ctx.startLiveKit();
            return;
        }

        if (ctx.supportsWebRTC && ctx.supportsWebRTC()) {
            await ctx.startWebRTC();
            return;
        }

        ctx.setupRecording();

        if (ctx.supportsLiveSpeech()) {
            setTimeout(() => ctx.startRecording(), 500);
        } else {
            setTimeout(() => {
                ctx.stopAudio();
                ctx.stopLoadingAnimation();
                ctx.showActionBtns();
            }, 500);
        }
    };

    ws.onmessage = (event) => ctx.handleMessage(event);

    ws.onclose = (event) => {
        const independentTransportActive = ctx.isIndependentTransportActive && ctx.isIndependentTransportActive();
        if (independentTransportActive) {
            debugLog('Bootstrap WebSocket closed while independent voice transport remains active', {
                code: event.code,
                reason: event.reason || null,
                wasClean: event.wasClean,
            });
            if (ctx.onSocketEvent) ctx.onSocketEvent('close', {
                code: event.code,
                wasClean: event.wasClean,
                willReconnect: false,
                independentTransportActive: true,
            });
            return;
        }
        const willReconnect = ctx.isModalOpen && event.code !== 1000 && ctx.reconnectAttempts.val < ctx.maxReconnectAttempts;
        debugLog('WebSocket disconnected', {
            code: event.code,
            reason: event.reason || null,
            wasClean: event.wasClean,
            online: navigator.onLine,
            visibilityState: document.visibilityState,
            modalOpen: ctx.isModalOpen,
        });
        if (ctx.onSocketEvent) ctx.onSocketEvent('close', {
            code: event.code,
            wasClean: event.wasClean,
            willReconnect: willReconnect,
            nextAttempt: willReconnect ? ctx.reconnectAttempts.val + 1 : null,
        });

        if (willReconnect) {
            ctx.reconnectAttempts.val++;
            debugLog(`Attempting to reconnect... (${ctx.reconnectAttempts.val}/${ctx.maxReconnectAttempts})`);
            setTimeout(() => {
                if (ctx.isModalOpen) {
                    startWebservice(ctx);
                }
            }, 2000 * ctx.reconnectAttempts.val);
        } else {
            ctx.stopRecording();
            if (ctx.isModalOpen && event.code !== 1000) {
                ctx.showError('Call disconnected. Tap Continue Conversation to reconnect.');
                ctx.showActionBtns();
                if (ctx.releaseReservation) ctx.releaseReservation();
            }
        }
    };

    ws.onerror = (error) => {
        if (ctx.isIndependentTransportActive && ctx.isIndependentTransportActive()) {
            debugLog('Bootstrap WebSocket error ignored because independent voice transport is active');
            return;
        }
        console.error("WebSocket error:", error);
        if (ctx.onSocketEvent) ctx.onSocketEvent('error', { readyState: ws.readyState });
        ctx.showError('Connection error. Please try again.');
    };

    return ws;
}


/**
 * Handle incoming WebSocket messages from the Python videochat server.
 * Dispatches each event type to the appropriate handler via the ctx callbacks.
 *
 * @param {MessageEvent} event
 * @param {object} ctx   All callbacks and mutable state
 */
export async function handleWebSocketMessage(event, ctx) {
    try {
        ctx.debugLog("📥 WebSocket message received:", {
            dataSize: `${Math.round(event.data.length / 1024)} KB`,
            timestamp: new Date().toISOString()
        });

        const data = JSON.parse(event.data);

        if (ctx.onMessageReceived) ctx.onMessageReceived(data, { payloadBytes: event.data.length });

        ctx.debugLog("📦 Parsed WebSocket data:", {
            type: data.type,
            hasContent: !!data.content,
            contentSize: data.content ? `${Math.round(JSON.stringify(data.content).length / 1024)} KB` : 'N/A',
            hasError: !!data.error
        });

        if (routeServerError(data, ctx)) {
            return;
        }

        if (data.type === 'text' || data.type === 'text_chunk') {
            window.__aiResponseTranscript = (window.__aiResponseTranscript || "") + data.content;
            ctx.debugLog("📝 AI text response received", { contentLength: data.content.length });
        }

        if (data.type === 'livekit_session_ready') {
            ctx.debugLog('✅ LiveKit room credentials received', {
                provider: data.provider,
                room: data.room,
            });
            await ctx.connectLiveKitSession(data);
        } else if (data.type === 'livekit_error') {
            ctx.handleLiveKitError(data.message || 'Unable to start the LiveKit voice call.');
        } else if (data.type === 'live_call_limit_reached') {
            ctx.onLiveCallLimitReached(data.message || 'Your paid Live Call allowance has ended.');
        } else if (data.type === 'session_started') {
            ctx.debugLog("✅ Persistent OpenAI session started");
            ctx.sessionState.started = true;
            ctx.sessionState.startInProgress = false;
        } else if (data.type === 'webrtc_answer') {
            ctx.debugLog("✅ WebRTC SDP answer received", { service: data.service });
            await ctx.acceptWebRTCAnswer(data.sdp);
        } else if (data.type === 'webrtc_error') {
            ctx.handleWebRTCError(data.message || 'Unable to start the modern live call.');
        } else if (data.type === 'speech_started') {
            ctx.debugLog("🎤 OpenAI detected speech started");
            if (ctx.uiState.openingResponsePending) {
                ctx.debugLog("Opening response interrupted by user; enabling normal captions");
                ctx.uiState.openingResponsePending = false;
                window.__aiResponseTranscript = "";
            }
            ctx.onUserSpeechStarted();
            window.__userTranscript = "";
            ctx.stopAudio();
        } else if (data.type === 'speech_stopped') {
            ctx.debugLog("🔇 OpenAI detected speech stopped - generating response...");
            window.__aiResponseTranscript = "";
            if (ctx.onUserSpeechStopped) ctx.onUserSpeechStopped();
        } else if (data.type === 'audio_response') {
            ctx.debugLog("🔊 Audio response chunk received");
            await ctx.handleAudio(data.content);
        } else if (data.type === 'text' || data.type === 'text_chunk') {
            if (ctx.uiState.openingResponsePending) {
                ctx.debugLog("Opening response transcript kept audio-only", {
                    contentLength: data.content ? data.content.length : 0,
                });
            } else if (ctx.infoTextContainer) {
                if (!ctx.uiState.aiResponseStarted) {
                    ctx.infoTextContainer.text('');
                    ctx.uiState.aiResponseStarted = true;
                }
                ctx.infoTextContainer.append(data.content);
            }
        } else if (data.type === 'user_transcription') {
            ctx.debugLog("🎤 User transcription received", { contentLength: String(data.content || '').length });
            window.__userTranscript = data.content;
            if (ctx.infoTextContainer) {
                ctx.infoTextContainer.text(`You: " ${data.content} "`).addClass('is-active');
                ctx.uiState.aiResponseStarted = false;
            }
        } else if (data.type === 'response_complete') {
            ctx.debugLog("✅ OpenAI response complete");
            ctx.uiState.aiResponseStarted = false;
            if (ctx.uiState.openingResponsePending) {
                ctx.debugLog("Opening response completed as audio-only; skipping chat text rendering");
                ctx.uiState.openingResponsePending = false;
                window.__userTranscript = "";
                window.__aiResponseTranscript = "";
            } else {
                ctx.saveMessages();
            }
            ctx.setCurrentTranscription("");
        } else if (data.type === 'session_ended') {
            ctx.debugLog("🛑 Persistent session ended");
        } else if ((data.type === 'audio' || data.type === 'audio_chunk')) {
            ctx.debugLog("🔊 Audio response received, processing...");
            await ctx.handleAudio(data.content);
        } else if (data.type === 'token_usage') {
            ctx.debugLog("💰 Token usage:", data.total_tokens);
            await ctx.handleTokenUsage(data.total_tokens);
            ctx.handleAwardXp();
        } else if (data.type === 'response_completed') {
            ctx.debugLog("✅ Response completed from server");
            ctx.uiState.aiResponseStarted = false;
            ctx.saveMessages();
            ctx.onAssistantResponseCompleted();
        } else if (data.type === 'follow_up_requested') {
            ctx.debugLog('Silence follow-up accepted by server', { attempt: data.attempt });
        } else if (data.type === 'follow_up_error') {
            ctx.onFollowUpError(data.message || 'Silence follow-up could not be started.');
        } else if (data.type === 'audio_completed') {
            ctx.debugLog("✅ Audio streaming completed from server");
        } else {
            ctx.debugLog("⚠️ Unknown message type", {
                type: data.type || null,
                keys: Object.keys(data),
            });
        }
    } catch (error) {
        console.error("❌ Error parsing WebSocket message:", {
            name: error && error.name,
            message: error && error.message,
            payloadBytes: event && event.data ? event.data.length : 0,
        });
    }
}


/**
 * Initialize the videocall session via AJAX, then start audio monitoring
 * and open the WebSocket connection.
 *
 * @param {object} ctx
 * @param {number} ctx.timeoutId           Mutable timeout ref
 * @param {XMLHttpRequest} ctx.ajaxRequest Mutable AJAX ref
 * @param {string} ctx.sessionToken
 * @param {string} ctx.sessionDomain       Mutable
 * @param {string} ctx.liveCallAccessToken Mutable
 * @param {function} ctx.debugLog
 * @param {function} ctx.initAudioLevel
 * @param {object} ctx.audioLevelCtx
 * @param {function} ctx.startWebservice
 * @param {function} ctx.handleAjaxError
 */
export function postToAjax(ctx, options = {}) {
    const shouldConnect = options.connect !== false;
    ctx.debugLog("Scheduling videocall AJAX bootstrap");
    if (ctx.timeoutId) clearTimeout(ctx.timeoutId);

    ctx.timeoutId = setTimeout(function () {
        if (ctx.ajaxRequest && ctx.ajaxRequest.readyState !== 4) return;

        ctx.ajaxRequest = $.ajax({
            url: growtype_chat_ajax.url,
            type: 'post',
            data: {
                session_token: ctx.sessionToken,
                action: growtype_chat_ajax.videocall_action,
                live_avatar_enabled: ctx.liveAvatarEnabled ? 1 : 0,
                nonce: growtype_chat_ajax.nonce,
            },
            success: async function (response) {
                ctx.debugLog("Videocall AJAX response received", {
                    success: !!response.success,
                    hasDomain: !!(response.data && response.data.domain),
                });
                if (response.success) {
                    if (response.data && response.data.domain) {
                        ctx.sessionDomain = atob(response.data.domain);
                    }
                    ctx.liveCallAccessToken = response.data && response.data.live_call_access_token
                        ? response.data.live_call_access_token
                        : '';
                    ctx.liveCallReservationId = response.data && response.data.reservation_id
                        ? response.data.reservation_id
                        : '';
                    if (!shouldConnect) return;
                    ctx.debugLog("✅ AJAX success, initializing audio and websocket...");
                    try {
                        const ok = await ctx.initAudioLevel(ctx.audioLevelCtx);
                        ctx.debugLog(ok ? "✅ Audio-level fallback monitor ready" : "⚠️ Audio-level fallback monitor unavailable");
                    } catch (err) { console.error("❌ Failed to initialize audio monitoring:", err); }
                    ctx.startWebservice();
                }
            },
            error: function (response) {
                const modal = $('#growtypeChatVideoCallModal');
                if (modal.find('.modal-videocall-avatar-setting').attr('data-available') === '1') {
                    modal.find('input[name="growtype_chat_live_avatar_enabled"]').prop('disabled', false);
                }
                ctx.handleAjaxError(response, $('.g-chat-btn-videocall'));
            }
        });
    }, 500);
}
